import { useCallback, useEffect, useId, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils.ts';
import { isInTopLayer } from '../../lib/modal-focus.ts';

/**
 * Tooltip (shadcn-Verhalten, von Hand wie `Popover`; DESIGN „Seitenleiste eingeklappt“, EXPERIENCE §4.1, §7): kleine
 * dunkle Pille mit dem Namen, erscheint bei Hover **und** Tastaturfokus, Esc blendet sie aus; Scrollen schließt sie bei der Maus, bei Tastaturfokus folgt sie dem Element. Liegt fest im Fenster
 * (Portal), damit eine Seitenleiste mit eigener Scroll-Fläche sie nicht abschneidet. Der Name selbst steht als
 * `aria-label` am Bedienelement; der Tooltip zeigt ihn nur sichtbar (`role="tooltip"`).
 */
/** So lange bleibt ein per Fingertipp geöffneter Tooltip stehen (Anzeige-Zeit der Oberfläche). */
const TOUCH_TOOLTIP_MS = 5000;

/** Höchstens ein Tooltip zugleich: ein neuer (Hover) schließt den vorigen (z. B. vom Tastaturfokus). */
let closeCurrent: (() => void) | null = null;

export function Tooltip({
  label,
  side = 'right',
  children,
  className,
  disabled,
  wrap = false,
  touch = false,
}: {
  label: string;
  /**
   * Längerer Text (z. B. Kundenkommentar, Story 7.18): umbrechen statt einer Zeile, höchstens 3 Zeilen, Rest „…“ (CSS),
   * nie breiter als das Fenster.
   */
  wrap?: boolean;
  /** Kein Tooltip (z. B. wenn der Name schon sichtbar daneben steht); das Bedienelement bleibt dasselbe. */
  disabled?: boolean;
  side?: 'right' | 'bottom';
  /**
   * Auch per Fingertipp (z. B. Last-Säulen ohne gedruckte Zahl, Story 23.9): Tippen blendet ein bzw. aus, Tippen
   * daneben oder Scrollen schließt. Ohne: nur Maus-Hover und Tastaturfokus.
   */
  touch?: boolean;
  /** Genau ein Bedienelement (Link oder Knopf). */
  children: ReactNode;
  className?: string;
}) {
  const wrapper = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const id = useId();

  // Fingertipp-Tooltip schließt nach einer Weile von selbst (wie ein Hinweis auf dem Telefon), falls nicht vorher getippt
  // oder gescrollt wird. Reine Anzeige-Zeit, keine fachliche Frist.
  const touchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTouchTimer = useCallback(() => {
    if (touchTimer.current) clearTimeout(touchTimer.current);
    touchTimer.current = null;
  }, []);
  const hide = useCallback(() => {
    clearTouchTimer();
    setPos(null);
  }, [clearTouchTimer]);

  /** Setzt den Tooltip an die aktuelle Lage des Bedienelements (fest im Fenster, daher nach jedem Scrollen neu). */
  const place = useCallback(() => {
    const rect = wrapper.current?.getBoundingClientRect();
    if (!rect) return;
    setPos(side === 'right' ? { x: rect.right + 10, y: rect.top + rect.height / 2 } : { x: rect.left + rect.width / 2, y: rect.bottom + 8 });
  }, [side]);

  function show() {
    if (disabled || !wrapper.current) return;
    if (closeCurrent !== hide) closeCurrent?.();
    closeCurrent = hide;
    place();
  }
  useEffect(
    () => () => {
      clearTouchTimer();
      if (closeCurrent === hide) closeCurrent = null;
    },
    [hide, clearTouchTimer],
  );

  useEffect(() => {
    if (!pos) return;
    const onKey = (e: KeyboardEvent) => {
      // Nur, wenn der Tooltip zur obersten Ebene gehört (sonst gehört Esc dem offenen Dialog darüber).
      if (e.key === 'Escape' && isInTopLayer(wrapper.current)) setPos(null);
    };
    // Maus: Scrollen schließt. Tastaturfokus: Das Fokussieren scrollt das Element oft selbst ins Bild (das Ereignis
    // kommt erst im nächsten Bild, nach dem Öffnen) – dann folgt der Tooltip dem Element, statt sich zu schließen.
    const onScroll = () => {
      if (wrapper.current?.querySelector(':focus-visible')) place();
      else setPos(null);
    };
    // Fingertipp-Tooltip: Tippen daneben schließt.
    const onOutside = (e: PointerEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setPos(null);
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    if (touch) document.addEventListener('pointerdown', onOutside, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('pointerdown', onOutside, true);
    };
  }, [pos, place, touch]);

  return (
    <span
      ref={wrapper}
      className={cn('inline-flex', className)}
      // Nur echte Maus: ein Fingertipp löst danach noch nachgestellte Maus-Ereignisse aus (Tooltip bliebe stehen).
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') show();
      }}
      // Fingertipp-Tooltip (`touch`): Beim Anheben des Fingers meldet der Browser `pointerleave` – das darf ihn nicht gleich
      // wieder schließen (Story 23.17, Schloss im Bezug; gilt für jeden `touch`-Tooltip, z. B. auch die Last-Säulen).
      // Es schließen: Tippen daneben, zweites Tippen, Scrollen, Esc und nach `TOUCH_TOOLTIP_MS` von selbst. Maus und
      // Tooltips ohne `touch` schließen wie bisher beim Verlassen.
      onPointerLeave={(e) => {
        if (!touch || e.pointerType === 'mouse') hide();
      }}
      onFocus={(e) => {
        // Nur bei Tastaturfokus (wie der Fokusring), nicht nach einem Klick mit Maus oder Finger.
        if ((e.target as HTMLElement).matches?.(':focus-visible')) show();
      }}
      onBlur={hide}
      onPointerDown={(e) => {
        if (touch && e.pointerType !== 'mouse') {
          if (pos) hide();
          else {
            show();
            touchTimer.current = setTimeout(hide, TOUCH_TOOLTIP_MS);
          }
        } else hide();
      }}
    >
      {children}
      {pos ? <TooltipBubble id={id} label={label} side={side} wrap={wrap} pos={pos} /> : null}
    </span>
  );
}

/** Die sichtbare Pille (fest im Fenster, per Portal); gemeinsam für `Tooltip` und `TooltipGroup`. */
function TooltipBubble({ id, label, side, wrap, pos }: { id: string; label: string; side: 'right' | 'bottom'; wrap: boolean; pos: { x: number; y: number } }) {
  return createPortal(
    <span
      id={id}
      role="tooltip"
      data-testid="tooltip"
      className={cn(
        'pointer-events-none fixed z-[60] bg-ink px-space-3 py-space-2 text-body text-on-ink shadow-float',
        wrap ? 'line-clamp-3 w-max max-w-tip rounded-tile break-words whitespace-pre-line' : 'rounded-full whitespace-nowrap',
        side === 'right' ? '-translate-y-1/2' : '-translate-x-1/2',
      )}
      style={{ left: pos.x, top: pos.y }}
    >
      {label}
    </span>,
    document.body,
  );
}

/** Lage der Pille an einem Element (rechts daneben bzw. mittig darunter). */
function placeAt(el: Element, side: 'right' | 'bottom'): { x: number; y: number } {
  const rect = el.getBoundingClientRect();
  return side === 'right' ? { x: rect.right + 10, y: rect.top + rect.height / 2 } : { x: rect.left + rect.width / 2, y: rect.bottom + 8 };
}

/**
 * Ein Tooltip für viele Bedienelemente nebeneinander (z. B. die bis zu 20 Symbole der Ampel-Zelle): Jedes Element trägt
 * seinen Text in `data-tooltip`; die Pille erscheint an dem Element unter der Maus bzw. mit Tastaturfokus – Aussehen und
 * Verhalten wie `Tooltip` (Esc, Scrollen, höchstens einer zugleich), ohne Fingertipp. Grund: Ein `Tooltip` je Symbol
 * kostete beim Aufbau großer Listen spürbar Zeit (50 Zeilen × 20 Symbole; Lasttest orders-browser).
 */
export function TooltipGroup({
  side = 'bottom',
  wrap = false,
  children,
  ...rest
}: {
  side?: 'right' | 'bottom';
  wrap?: boolean;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLSpanElement>, 'children'>) {
  const wrapper = useRef<HTMLSpanElement>(null);
  const anchor = useRef<HTMLElement | null>(null);
  const [tip, setTip] = useState<{ label: string; pos: { x: number; y: number } } | null>(null);
  const id = useId();
  const hide = useCallback(() => {
    anchor.current = null;
    setTip(null);
  }, []);

  function show(el: HTMLElement) {
    const label = el.dataset.tooltip;
    if (!label) return;
    if (closeCurrent !== hide) closeCurrent?.();
    closeCurrent = hide;
    anchor.current = el;
    setTip({ label, pos: placeAt(el, side) });
  }
  const anchorOf = (target: EventTarget | null): HTMLElement | null => {
    const el = target instanceof Element ? target.closest<HTMLElement>('[data-tooltip]') : null;
    return el && wrapper.current?.contains(el) ? el : null;
  };
  useEffect(
    () => () => {
      if (closeCurrent === hide) closeCurrent = null;
    },
    [hide],
  );

  const open = tip !== null;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Nur, wenn der Tooltip zur obersten Ebene gehört (sonst gehört Esc dem offenen Dialog darüber).
      if (e.key === 'Escape' && isInTopLayer(wrapper.current)) hide();
    };
    // Wie `Tooltip`: Maus – Scrollen schließt; Tastaturfokus – die Pille folgt dem Element.
    const onScroll = () => {
      const el = anchor.current;
      if (el?.matches(':focus-visible')) setTip((t) => (t ? { ...t, pos: placeAt(el, side) } : t));
      else hide();
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, hide, side]);

  return (
    <span
      {...rest}
      ref={wrapper}
      // Nur echte Maus (wie `Tooltip`); zwischen den Elementen (Lücke) keine Pille.
      onPointerOver={(e) => {
        if (e.pointerType !== 'mouse') return;
        const el = anchorOf(e.target);
        if (!el) hide();
        else if (el !== anchor.current) show(el);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') hide();
      }}
      onFocus={(e) => {
        // Nur bei Tastaturfokus (wie der Fokusring), nicht nach einem Klick mit Maus oder Finger.
        const el = anchorOf(e.target);
        if (el && el.matches(':focus-visible')) show(el);
      }}
      onBlur={hide}
      onPointerDown={hide}
    >
      {children}
      {tip ? <TooltipBubble id={id} label={tip.label} side={side} wrap={wrap} pos={tip.pos} /> : null}
    </span>
  );
}
